'use client';

// Global saved-view alert watcher. Mounted once in the app layout (inside
// AuthProvider) so it fires on ANY page — the scanner, the dashboard, anywhere
// ("also push on dashboard too"). For each saved view the user has toggled an
// alert on (stored per-device in localStorage), it polls that view's own
// filters via the scanner-log endpoint and fires a browser notification +
// in-app toast (+ optional chime) on genuinely-fresh matches.
//
// Polling each enabled view by its OWN filters reuses the backend's exact
// filter semantics, so we never re-implement the WHERE-clause logic client-side
// (the matching-divergence trap). Typical user has 1–3 enabled views.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  adminListScannerSavedSearches,
  adminGetScannerLog,
  type ScannerSavedSearch,
  type ScannerLogEntry,
} from '@/lib/api-client';
import { REASON_LABEL } from '@/lib/chart/scannerMarkers';
import { getAllViewAlerts, setViewAlert, subscribeViewAlerts } from '@/lib/savedViewAlerts';
import {
  savedFiltersToQuery,
  computeFreshHits,
  newAlertWatchState,
  type AlertWatchState,
} from '@/lib/scannerAlertDiff';
import { playChime } from '@/lib/alertSound';
import { emitAlertToast } from '@/lib/alertToast';

const POLL_MS = 30_000;
const PAUSED_RECHECK_MS = 5 * 60_000;
/** Re-fetch the saved-view definitions every ~N ticks (~5 min) so renames /
 *  filter edits are reflected without the user toggling the alert off and on. */
const SEARCHES_RELOAD_EVERY = 10;
const PAGE_LIMIT = 50;

function enabledKeyFrom(): string {
  const all = getAllViewAlerts();
  return Object.keys(all)
    .filter((id) => all[id]?.alert)
    .sort()
    .join(',');
}

function fire(view: ScannerSavedSearch, fresh: ScannerLogEntry[], audio: boolean): void {
  const top = fresh[0];
  const chg = top.liveChangePct ?? top.currentChangePct;
  const price = top.livePrice ?? top.currentPrice;
  const sign = chg >= 0 ? '+' : '';
  const title = fresh.length === 1
    ? `${top.ticker} ${sign}${chg.toFixed(1)}%`
    : `${fresh.length} new hits`;
  const body = fresh.length === 1
    ? `${REASON_LABEL[top.qualificationReason] ?? top.qualificationReason} · $${price.toFixed(2)}`
    : fresh.slice(0, 4).map((r) => r.ticker).join(', ');

  // In-app toast — the cue that shows up wherever the user is.
  emitAlertToast({ viewName: view.name, title, body });

  // OS notification (best-effort; permission was requested when the bell was
  // first enabled). The tag collapses repeated alerts for the same view.
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      new Notification(`${view.name} — ${title}`, {
        body,
        tag: `scanner-alert-${view.id}`,
        icon: '/favicon.ico',
      });
    }
  } catch {
    /* ignore */
  }

  if (audio) playChime();
}

export default function SavedViewAlertsWatcher() {
  const { user } = useAuth();
  const [enabledKey, setEnabledKey] = useState<string>(() =>
    (typeof window === 'undefined' ? '' : enabledKeyFrom()),
  );
  const enabledIds = useMemo(() => (enabledKey ? enabledKey.split(',') : []), [enabledKey]);

  // Mutable refs the interval reads without re-subscribing.
  const searchesRef = useRef<Map<string, ScannerSavedSearch>>(new Map());
  const stateRef = useRef<Map<string, AlertWatchState>>(new Map());
  const lastFullPollRef = useRef(0);
  const scannerActiveRef = useRef(true);

  // React to localStorage config changes (this tab + other tabs).
  useEffect(() => subscribeViewAlerts(() => setEnabledKey(enabledKeyFrom())), []);

  // One effect owns the whole loop: load the view definitions, take baselines
  // immediately, then poll on the cadence. Re-runs when the enabled SET or the
  // user changes (audio-only toggles don't change enabledKey, so they don't
  // churn this effect — the audio flag is re-read fresh each tick).
  useEffect(() => {
    if (!user || enabledIds.length === 0) return;
    let stopped = false;

    // Prune state for views no longer enabled so a re-enabled view re-baselines
    // (never dumps its backlog).
    for (const id of Array.from(stateRef.current.keys())) {
      if (!enabledIds.includes(id)) stateRef.current.delete(id);
    }

    async function loadSearches(): Promise<void> {
      try {
        const list = await adminListScannerSavedSearches();
        const m = new Map<string, ScannerSavedSearch>();
        for (const s of list.searches) m.set(s.id, s);
        searchesRef.current = m;
        // Drop orphaned localStorage entries (the underlying view was deleted)
        // so we stop polling an id that no longer exists.
        const live = new Set(list.searches.map((s) => s.id));
        for (const id of enabledIds) {
          if (!live.has(id)) setViewAlert(id, { alert: false, audio: false });
        }
      } catch {
        /* not Full tier / offline — watcher just no-ops */
      }
    }

    async function pollOne(view: ScannerSavedSearch, audio: boolean): Promise<void> {
      const base = savedFiltersToQuery(view.filters);
      let st = stateRef.current.get(view.id);
      if (!st) {
        st = newAlertWatchState();
        stateRef.current.set(view.id, st);
      }
      try {
        if (st.baselineMs == null) {
          // Baseline pass — grab just the newest existing row to set the
          // watermark; no alert, so enabling never dumps the backlog.
          const page = await adminGetScannerLog({ ...base, limit: 1, sortBy: 'detectedAt', sortDir: 'desc' });
          scannerActiveRef.current = page.scannerActive !== false;
          if (page.items.length > 0) computeFreshHits(page.items, st, Date.now());
          else st.baselineMs = Date.now(); // empty view → watermark at enable time so the first real hit alerts
          return;
        }
        // Incremental pass — OLDEST-first from the watermark + a `since` bound,
        // so a burst larger than one page is consumed across polls without ever
        // skipping a row (the count-bounded DESC approach could drop the tail).
        const page = await adminGetScannerLog({
          ...base,
          since: new Date(st.baselineMs).toISOString(),
          limit: PAGE_LIMIT,
          sortBy: 'detectedAt',
          sortDir: 'asc',
        });
        scannerActiveRef.current = page.scannerActive !== false;
        const fresh = computeFreshHits(page.items, st, Date.now());
        if (fresh.length > 0) fire(view, fresh, audio);
      } catch {
        /* per-view poll failure is local — ignore */
      }
    }

    let tickCount = 0;
    async function tick(): Promise<void> {
      if (stopped) return;
      const cfg = getAllViewAlerts();
      const views = enabledIds
        .map((id) => searchesRef.current.get(id))
        .filter((v): v is ScannerSavedSearch => !!v);
      // Initial baselines must happen promptly even off-hours — the back-off is
      // only meant to throttle re-polling for alerts, not first baselining.
      const needsBaseline = views.some((v) => stateRef.current.get(v.id)?.baselineMs == null);
      const now = Date.now();
      if (!needsBaseline && !scannerActiveRef.current && now - lastFullPollRef.current < PAUSED_RECHECK_MS) return;
      lastFullPollRef.current = now;
      tickCount += 1;
      if (tickCount % SEARCHES_RELOAD_EVERY === 0) void loadSearches();
      await Promise.all(views.map((v) => pollOne(v, cfg[v.id]?.audio ?? false)));
    }

    // Load the view defs, then take baselines right away (don't wait a full
    // cadence). The `stopped` guard bails if the effect was torn down meanwhile.
    void (async () => {
      await loadSearches();
      if (stopped) return;
      await tick();
    })();

    const interval = window.setInterval(tick, POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, [user, enabledIds]);

  return null;
}
